/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Link, Typography2 as Typography, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import CourseEnrollButton from 'bundles/enroll-common/components/CourseEnrollButton';
import TogglableContent from 'bundles/page/components/TogglableContent';
import RatingLine from 'bundles/reviews-common/components/cds/RatingLine';

import _t from 'i18n!nls/reviews';

const DESCRIPTION_LINE_HEIGHT = 20;

export type ReviewsHeaderRatings = {
  averageFiveStarRating: number;
  ratingCount: number;
  commentCount: number;
};

type Props = {
  courseName: string;
  courseId: string;
  programSlug?: string;
  coursePageHref: string;
  ratings?: ReviewsHeaderRatings | null;
  description?: string | null;
};

const styles = {
  header: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    margin-top: var(--cds-spacing-400);
  `,
  title: css`
    font-weight: 600;
  `,
  enrollButton: css`
    margin-bottom: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-50);
    width: 30%;
    height: 36px;
    ${breakpoints.down('sm')} {
      width: auto;
      min-width: min-content;
    }
  `,
  ratingDate: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  dotSeparator: css`
    margin: 0 var(--cds-spacing-100);
  `,
  aboutTextContent: css`
    width: 100%;

    .content-inner p {
      margin: var(--cds-spacing-100) 0;
      color: var(--cds-color-neutral-primary-weak);
      line-height: ${DESCRIPTION_LINE_HEIGHT}px;
      font-size: 14px;
    }

    .toggle-button-wrapper {
      margin-top: 0;
    }
  `,
  aboutToggleText: css`
    text-decoration: underline;
    font-size: 14px;
  `,
  aboutContainer: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
  `,
};

const ReviewsHeader = ({ courseName, courseId, programSlug, coursePageHref, ratings, description }: Props) => {
  return (
    <div data-testid="reviews-header" css={styles.header}>
      <Typography variant="subtitleLarge" component="h2" css={styles.title}>
        {_t('Learner reviews & feedback for ')}
        <Link href={coursePageHref} variant="standardSecondary">
          {courseName}
        </Link>
      </Typography>
      <RatingLine rating={ratings?.averageFiveStarRating ?? 0} numReviews={ratings?.ratingCount ?? 0} />
      {!programSlug && (
        <CourseEnrollButton
          courseId={courseId}
          showStartDate={false}
          enrollButtonText={_t('Enroll for free')}
          renderEnrollButton={({ onClick, disabled, enrollButtonText: text, href, ariaLabel }) =>
            href ? (
              <Button
                component="a"
                href={href}
                variant="primary"
                size="small"
                css={styles.enrollButton}
                icon={<ArrowNextIcon />}
                aria-label={ariaLabel}
              >
                {text}
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={onClick}
                disabled={disabled}
                size="small"
                css={styles.enrollButton}
                icon={<ArrowNextIcon />}
                aria-label={ariaLabel}
              >
                {text}
              </Button>
            )
          }
        />
      )}

      {description && (
        <div css={styles.aboutContainer} aria-live="polite">
          <Typography variant="subtitleSmall" component="h3">
            {_t('About this course')}
          </Typography>
          <div css={styles.aboutTextContent} data-testid="reviews-header-description">
            <TogglableContent
              collapsedHeight={70}
              childrenWrapperTag="div"
              disableCollapseScroll
              renderToggleButtonContentFunction={({ collapsed }) => (
                <Typography
                  component="span"
                  variant="bodySecondary"
                  css={styles.aboutToggleText}
                  data-testid="reviews-header-description-toggle"
                >
                  {collapsed ? _t('Show more') : _t('Show less')}
                </Typography>
              )}
              toggleButtonType="button"
            >
              {description}
            </TogglableContent>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsHeader;
